import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useQuery } from '@tanstack/react-query';
import { fetchRadar, fetchSatellite } from '../lib/api';
import { lightBasemapSource } from '../lib/basemap';
import type { RadarFrame } from '../types';

const FRAME_INTERVAL_MS = 600;

// GIBS day/night layer — server probes for a working timestamp. Max zooms
// differ per layer (GeoColor: 7, IR: 6), so the maxzoom cap comes from
// here and must match what the server endpoint embeds in the template.
const GIBS_MAXZOOM = {
  geoColor: 7,
  infrared: 6,
} as const;

type MapLayer = 'radar' | 'satellite' | 'both';

export default function RadarMap({
  centerLat,
  centerLon,
  sunrise,
  sunset,
}: {
  centerLat: number;
  centerLon: number;
  sunrise: string | null;
  sunset: string | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [layer, setLayer] = useState<MapLayer>('radar');

  // Auto-pick GeoColor (day) or Clean Infrared (night) based on sun times
  // with a 30-min grace band on either side.
  const isDaytime = (() => {
    if (!sunrise || !sunset) return true;
    const now = Date.now();
    return (
      now >= new Date(sunrise).getTime() - 30 * 60_000 &&
      now <= new Date(sunset).getTime() + 30 * 60_000
    );
  })();

  const radar = useQuery({
    queryKey: ['radar'],
    queryFn: fetchRadar,
    refetchInterval: 8 * 60 * 1000,
  });
  const satellite = useQuery({
    queryKey: ['satellite'],
    queryFn: fetchSatellite,
    refetchInterval: 5 * 60 * 1000,
  });

  const gibsMeta = isDaytime ? satellite.data?.geoColor : satellite.data?.infrared;
  const satelliteMaxZoom = isDaytime ? GIBS_MAXZOOM.geoColor : GIBS_MAXZOOM.infrared;
  const satelliteAttribution = isDaytime
    ? 'GOES-West GeoColor · NASA GIBS'
    : 'GOES-West IR (Band 13) · NASA GIBS';
  // All available timestamps, oldest-first so the animation moves forward in
  // time like a movie. The server returns them newest-first.
  const satTimestamps = gibsMeta?.timestamps?.slice().reverse() ?? [];
  const satFrameCount = satTimestamps.length;
  const [satIdx, setSatIdx] = useState(0);

  // Advance satellite frame in lockstep with radar playback.
  useEffect(() => {
    if (satFrameCount === 0) return;
    if (!playing) return;
    const id = window.setInterval(
      () => setSatIdx((i) => (i + 1) % satFrameCount),
      FRAME_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [playing, satFrameCount]);

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          basemap: lightBasemapSource,
        },
        layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }],
      },
      center: [centerLon, centerLat],
      zoom: 7,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [centerLat, centerLon]);

  // Default index = most recent past frame
  useEffect(() => {
    if (!radar.data) return;
    const past = radar.data.frames.filter((f) => f.kind === 'past');
    setIdx(Math.max(0, past.length - 1));
  }, [radar.data]);

  // Satellite frames — one source + layer per timestamp. All preload tiles
  // at mount; animation is just raster-opacity toggling, which MapLibre
  // handles at the paint layer without any tile fetches. This is what makes
  // multi-frame satellite animation robust where `setTiles()` wasn't.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gibsMeta) return;
    const apply = () => {
      // Clean up any previous satellite-frame-* layers (happens on
      // timestamp refresh or day/night swap).
      const style = map.getStyle();
      for (const L of style.layers ?? []) {
        if (L.id.startsWith('sat-frame-')) map.removeLayer(L.id);
      }
      for (const sid of Object.keys(style.sources ?? {})) {
        if (sid.startsWith('sat-frame-')) map.removeSource(sid);
      }

      if (layer !== 'satellite' && layer !== 'both') return;
      if (satTimestamps.length === 0) return;

      const beforeId = map.getLayer('radar') ? 'radar' : undefined;
      satTimestamps.forEach((ts, i) => {
        const srcId = `sat-frame-${i}`;
        map.addSource(srcId, {
          type: 'raster',
          tiles: [gibsMeta.tileUrlTemplate.replace('{time}', ts)],
          tileSize: 256,
          minzoom: 0,
          maxzoom: satelliteMaxZoom,
          attribution: i === 0 ? satelliteAttribution : '',
        });
        map.addLayer(
          {
            id: srcId,
            type: 'raster',
            source: srcId,
            paint: { 'raster-opacity': 0 },
          },
          beforeId,
        );
      });
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, gibsMeta, satelliteMaxZoom, satelliteAttribution]);

  // Animate: toggle opacity so only satIdx's layer is visible.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || satFrameCount === 0) return;
    const targetOpacity = layer === 'both' ? 0.55 : 0.85;
    for (let i = 0; i < satFrameCount; i++) {
      const id = `sat-frame-${i}`;
      if (!map.getLayer(id)) continue;
      map.setPaintProperty(id, 'raster-opacity', i === satIdx ? targetOpacity : 0);
    }
  }, [satIdx, satFrameCount, layer]);

  // Radar layer management — animates with the frame index.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const radarFrame = radar.data?.frames?.[idx];
    const radarUrl =
      radarFrame && radar.data
        ? radar.data.tileUrlFormat
            .replace('{host}', radar.data.host)
            .replace('{path}', radarFrame.path)
        : null;

    const apply = () => {
      if (map.getLayer('radar')) map.removeLayer('radar');
      if (map.getSource('radar')) map.removeSource('radar');
      if ((layer === 'radar' || layer === 'both') && radarUrl) {
        map.addSource('radar', {
          type: 'raster',
          tiles: [radarUrl],
          tileSize: 256,
          minzoom: 0,
          // RainViewer public tiles only serve actual radar data up to z=7.
          maxzoom: 7,
        });
        map.addLayer({
          id: 'radar',
          type: 'raster',
          source: 'radar',
          paint: { 'raster-opacity': 0.7 },
        });
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [idx, radar.data, layer]);

  // Playback
  useEffect(() => {
    if (!playing || !radar.data || radar.data.frames.length === 0) return;
    const id = window.setInterval(
      () => setIdx((i) => (i + 1) % radar.data!.frames.length),
      FRAME_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [playing, radar.data]);

  const frame: RadarFrame | undefined = radar.data?.frames[idx];

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs uppercase tracking-wider opacity-70">
          {layer === 'satellite' ? 'Satellite' : layer === 'both' ? 'Radar + Satellite' : 'Radar'}
        </h2>
        <div className="flex gap-px rounded overflow-hidden text-[10px] uppercase tracking-wider">
          {(['radar', 'satellite', 'both'] as const).map((opt) => (
            <button
              key={opt}
              onClick={() => setLayer(opt)}
              className={`px-2 py-1 ${
                layer === opt ? 'bg-white/30' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-lg overflow-hidden bg-slate-900 border border-white/10">
        <div ref={containerRef} className="h-80 w-full" />
        <div className="flex items-center gap-3 p-2 bg-black/60 text-xs">
          <button
            onClick={() => setPlaying((p) => !p)}
            className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 transition"
          >
            {playing ? 'Pause' : 'Play'}
          </button>
          <input
            type="range"
            min={0}
            max={Math.max(0, (radar.data?.frames.length ?? 1) - 1)}
            value={idx}
            onChange={(e) => {
              setPlaying(false);
              setIdx(Number(e.target.value));
            }}
            className="flex-1"
          />
          <span className="tabular-nums w-32 text-right">
            {frame ? new Date(frame.time * 1000).toLocaleTimeString() : '—'}
            {frame?.kind === 'nowcast' && (
              <span className="ml-1 text-amber-300">forecast</span>
            )}
          </span>
        </div>
      </div>
    </section>
  );
}
